import React, { useState } from 'react';
import { Shop, Language } from '../types';
import { Save, MapPin, Phone, Clock, FileText, Store, Upload, Sparkles, Navigation, AlertCircle, Percent, Loader2, Wand2 } from 'lucide-react';
import { SERVICE_CATEGORIES } from '../constants';
import { generateShopImage, generateLogoImage, improveDescription } from '../services/geminiService';
import { uploadFile, deleteFile } from '../services/supabaseClient';
import { useTranslation } from '../LanguageContext';

interface ShopSettingsProps {
  shop: Shop;
  onUpdateShop: (updatedShop: Shop) => void;
}

export const ShopSettings: React.FC<ShopSettingsProps> = ({ shop, onUpdateShop }) => {
  const { language } = useTranslation();
  const [formData, setFormData] = useState<Shop>({ ...shop });
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingCover, setIsGeneratingCover] = useState(false);
  const [isGeneratingLogo, setIsGeneratingLogo] = useState(false);
  const [isImprovingDescription, setIsImprovingDescription] = useState(false);

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Shop Settings', KH: 'ការកំណត់ហាង', ZH: '店铺设置', JA: 'ショップ設定', KO: '상점 설정' },
    sub: { EN: 'Update your business information and appearance.', KH: 'ធ្វើបច្ចុប្បន្នភាពព័ត៌មាន និងរូបរាងអាជីវកម្មរបស់អ្នក។', ZH: '更新您的业务信息和外观。', JA: 'ビジネス情報と外観を更新します。', KO: '비즈니스 정보 및 외관을 업데이트하십시오.' },
    cover: { EN: 'Cover Photo', KH: 'រូបភាពគម្រប', ZH: '封面照片', JA: 'カバー写真', KO: '커버 사진' },
    logo: { EN: 'Logo', KH: 'ឡូហ្គោ', ZH: '标志', JA: 'ロゴ', KO: '로고' },
    change_cover: { EN: 'Change Cover', KH: 'ប្តូររូបភាពគម្រប', ZH: '更改封面', JA: 'カバーを変更', KO: '커버 변경' },
    change_logo: { EN: 'Change Logo', KH: 'ប្តូរឡូហ្គោ', ZH: '更改标志', JA: 'ロゴを変更', KO: '로고 변경' },
    shop_name: { EN: 'Shop Name', KH: 'ឈ្មោះហាង', ZH: '店铺名称', JA: 'ショップ名', KO: '상점 이름' },
    vat: { EN: 'VAT Percentage', KH: 'ភាគរយអាករ (VAT)', ZH: '增值税百分比', JA: 'VATパーセンテージ', KO: '부가세 백분율' },
    categories: { EN: 'Categories Served', KH: 'ប្រភេទសេវាកម្ម', ZH: '服务类别', JA: '対応カテゴリ', KO: '제공 카테고리' },
    description: { EN: 'Description', KH: 'ការរៀបរាប់', ZH: '描述', JA: '説明', KO: '설명' },
    desc_placeholder: { EN: 'Tell customers why they should choose you...', KH: 'ប្រាប់អតិថិជនពីមូលហេតុដែលពួកគេគួរជ្រើសរើសអ្នក...', ZH: '告诉客户为什么他们应该选择您...', JA: 'お客様に選ばれる理由を伝えましょう...', KO: '고객이 당신을 선택해야 하는 이유를 설명하세요...' },
    phone: { EN: 'Phone Number', KH: 'លេខទូរស័ព្ទ', ZH: '电话号码', JA: '電話番号', KO: '전화번호' },
    hours: { EN: 'Working Hours', KH: 'ម៉ោងធ្វើការ', ZH: '营业时间', JA: '営業時間', KO: '영업 시간' },
    save: { EN: 'Save Changes', KH: 'រក្សាទុកការផ្លាស់ប្តូរ', ZH: '保存更改', JA: '変更を保存', KO: '변경 사항 저장' },
    saving: { EN: 'Saving...', KH: 'កំពុងរក្សាទុក...', ZH: '保存中...', JA: '保存中...', KO: '저장 중...' },
    ai_gen: { EN: 'Generate with AI', KH: 'បង្កើតដោយ AI', ZH: '使用 AI 生成', JA: 'AI で生成', KO: 'AI로 생성' },
    ai_regen: { EN: 'Regenerate AI', KH: 'បង្កើតថ្មីដោយ AI', ZH: '重新 AI 生成', JA: 'AI で再生成', KO: 'AI로 재생성' },
    ai_improve: { EN: 'Improve with AI', KH: 'កែលម្អដោយ AI', ZH: '使用 AI 改进', JA: 'AI で改善', KO: 'AI로 개선' },
    generating: { EN: 'Generating...', KH: 'កំពុងបង្កើត...', ZH: '生成中...', JA: '生成中...', KO: '생성 중...' },
    improving: { EN: 'Optimizing...', KH: 'កំពុងកែលម្អ...', ZH: '正在优化...', JA: '最適化中...', KO: '최적화 중...' }
  };

  const handleChange = (field: keyof Shop, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field: keyof typeof shop.contact, value: string) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const handleImageUpload = async (field: 'imageUrl' | 'logoUrl', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsSaving(true);
      try {
        const oldUrl = formData[field];
        if (oldUrl) await deleteFile(oldUrl);
        const path = `shops/${formData.id}/${field}_${Date.now()}`;
        const newUrl = await uploadFile(path, file);
        setFormData(prev => ({ ...prev, [field]: newUrl }));
      } catch (err) {
        console.error("Upload failed", err);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleGenerateCover = async () => {
    if (!formData.name) return;
    setIsGeneratingCover(true);
    const generatedImage = await generateShopImage(formData.name, formData.description);
    if (generatedImage) {
      if (formData.imageUrl) await deleteFile(formData.imageUrl);
      setFormData(prev => ({ ...prev, imageUrl: generatedImage }));
    }
    setIsGeneratingCover(false);
  };

  const handleGenerateLogo = async () => {
    if (!formData.name) return;
    setIsGeneratingLogo(true);
    const generatedLogo = await generateLogoImage(formData.name, formData.category);
    if (generatedLogo) {
      if (formData.logoUrl) await deleteFile(formData.logoUrl);
      setFormData(prev => ({ ...prev, logoUrl: generatedLogo }));
    }
    setIsGeneratingLogo(false);
  };

  const handleImproveDescription = async () => {
    if (!formData.name) return;
    setIsImprovingDescription(true);
    const improvedText = await improveDescription(formData.description, formData.name, formData.category, language);
    if (improvedText) {
      setFormData(prev => ({ ...prev, description: improvedText }));
    }
    setIsImprovingDescription(false);
  };

  const toggleCategory = (category: string) => {
    setFormData(prev => {
      const currentCategories = prev.category || [];
      if (currentCategories.includes(category)) {
        return { ...prev, category: currentCategories.filter(c => c !== category) };
      } else {
        return { ...prev, category: [...currentCategories, category] };
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    onUpdateShop(formData);
    setTimeout(() => setIsSaving(false), 500);
  };

  return (
    <div className="animate-in slide-in-from-bottom-4 duration-300 max-w-4xl mx-auto">
       <div className="bg-white rounded-[2.5rem] p-8 shadow-xl shadow-slate-200/50 border border-slate-100">
          <div className="flex items-center space-x-3 mb-8 border-b border-slate-50 pb-6">
             <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-slate-200">
                <Store size={24} />
             </div>
             <div>
               <h2 className="text-2xl font-black text-slate-900">{texts.title[language]}</h2>
               <p className="text-slate-500 font-medium">{texts.sub[language]}</p>
             </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex justify-between items-center">
                      <span>{texts.cover[language]}</span>
                      <button
                        type="button"
                        onClick={handleGenerateCover}
                        disabled={isGeneratingCover || !formData.name}
                        className={`flex items-center text-[10px] font-black transition-colors ${isGeneratingCover || !formData.name ? 'text-slate-300 cursor-not-allowed' : 'text-violet-600 hover:text-violet-700'}`}
                      >
                        {isGeneratingCover ? (
                           <>{texts.generating[language]}</>
                        ) : (
                           <><Sparkles size={12} className="mr-1" fill="currentColor"/> {formData.imageUrl ? texts.ai_regen[language] : texts.ai_gen[language]}</>
                        )}
                      </button>
                   </label>
                   <div className="relative h-48 rounded-2xl overflow-hidden group bg-slate-100 border-2 border-dashed border-slate-200">
                      <img src={formData.imageUrl} alt="Cover" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                         <div className="text-white font-black text-xs uppercase tracking-widest flex items-center">
                            <Upload size={20} className="mr-2" /> {texts.change_cover[language]}
                         </div>
                         <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={(e) => handleImageUpload('imageUrl', e)} />
                      </div>
                   </div>
                </div>

                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex justify-between items-center">
                      <span>{texts.logo[language]}</span>
                      <button
                        type="button"
                        onClick={handleGenerateLogo}
                        disabled={isGeneratingLogo || !formData.name}
                        className={`flex items-center text-[10px] font-black transition-colors ${isGeneratingLogo || !formData.name ? 'text-slate-300 cursor-not-allowed' : 'text-violet-600 hover:text-violet-700'}`}
                      >
                        {isGeneratingLogo ? <Loader2 size={12} className="animate-spin mr-1"/> : <Wand2 size={12} className="mr-1" fill="currentColor"/>}
                        {texts.ai_gen[language]}
                      </button>
                   </label>
                   <div className="relative h-48 rounded-2xl overflow-hidden group bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center">
                      <img src={formData.logoUrl} alt="Logo" className="w-32 h-32 object-contain" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                         <div className="text-white font-black text-xs uppercase tracking-widest flex items-center">
                            <Upload size={20} className="mr-2" /> {texts.change_logo[language]}
                         </div>
                         <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={(e) => handleImageUpload('logoUrl', e)} />
                      </div>
                   </div>
                </div>
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center">
                    <Store size={14} className="mr-1.5 text-violet-500"/> {texts.shop_name[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input 
                     type="text" 
                     value={formData.name} 
                     onChange={(e) => handleChange('name', e.target.value)}
                     className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all"
                   />
                </div>

                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center">
                    <Percent size={14} className="mr-1.5 text-violet-500"/> {texts.vat[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <div className="relative group">
                     <input 
                       type="number" 
                       min="0"
                       max="100"
                       step="0.01"
                       value={formData.vatPercentage} 
                       onChange={(e) => handleChange('vatPercentage', parseFloat(e.target.value) || 0)}
                       className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all pr-12"
                     />
                     <div className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 font-black">%</div>
                   </div>
                </div>
             </div>

             <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center">
                  <Store size={14} className="mr-1.5 text-violet-500"/> {texts.categories[language]} <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <div className="flex flex-wrap gap-2 p-5 rounded-2xl bg-slate-50 border border-slate-100">
                   {SERVICE_CATEGORIES.map(category => {
                     const isSelected = formData.category.includes(category);
                     return (
                       <button
                         key={category}
                         type="button"
                         onClick={() => toggleCategory(category)}
                         className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-2 ${
                           isSelected 
                             ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                             : 'bg-white text-slate-400 border-slate-100 hover:border-violet-200'
                         }`}
                       >
                         {category}
                       </button>
                     );
                   })}
                </div>
             </div>

             <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex justify-between items-center">
                   <div className="flex items-center">
                    <FileText size={14} className="mr-1.5 text-violet-500"/> {texts.description[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </div>
                   <button
                     type="button"
                     onClick={handleImproveDescription}
                     disabled={isImprovingDescription || !formData.name}
                     className={`flex items-center text-[10px] font-black transition-colors ${isImprovingDescription || !formData.name ? 'text-slate-300 cursor-not-allowed' : 'text-violet-600 hover:text-violet-700'}`}
                   >
                     {isImprovingDescription ? <Loader2 size={12} className="animate-spin mr-1"/> : <Sparkles size={12} className="mr-1" fill="currentColor"/>}
                     {formData.description.trim() ? texts.ai_improve[language] : texts.ai_gen[language]}
                   </button>
                </label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full p-6 rounded-2xl bg-slate-50 font-medium text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all min-h-[140px] resize-none shadow-inner"
                  placeholder={texts.desc_placeholder[language]}
                />
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center">
                    <Phone size={14} className="mr-1.5 text-violet-500"/> {texts.phone[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input 
                     type="text" 
                     value={formData.contact.phone} 
                     onChange={(e) => handleContactChange('phone', e.target.value)}
                     className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all"
                   />
                </div>
                <div className="space-y-2">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center">
                    <Clock size={14} className="mr-1.5 text-violet-500"/> {texts.hours[language]} <span className="text-rose-500 ml-0.5">*</span>
                   </label>
                   <input 
                     type="text" 
                     value={formData.workingHours} 
                     onChange={(e) => handleChange('workingHours', e.target.value)}
                     className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none focus:ring-4 focus:ring-violet-100 border-none transition-all"
                   />
                </div>
             </div>

             <div className="pt-6 flex justify-end">
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="px-10 py-5 bg-slate-900 text-white rounded-[1.8rem] font-black text-xs uppercase tracking-widest hover:bg-violet-600 shadow-2xl shadow-slate-900/20 transition-all flex items-center disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-95"
                >
                  {isSaving ? (
                    <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin"/> {texts.saving[language]}</span>
                  ) : (
                    <span className="flex items-center gap-2"><Save size={18} /> {texts.save[language]}</span>
                  )}
                </button>
             </div>
          </form>
       </div>
    </div>
  );
};